import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import ChatInterface from '../components/chat/ChatInterface';
import api from '../utils/api';

const ChatPage = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { conversations, setConversations, refreshConversations } = useOutletContext();
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(chatId);

  useEffect(() => {
    const loadMessages = async () => {
      if (chatId) {
        try {
          const response = await api.get(`/api/chat/conversations/${chatId}`);
          setMessages(response.data.messages || []);
          setCurrentConversationId(chatId);
        } catch (error) {
          console.error('Failed to load conversation:', error);
          const conversation = conversations.find(c => c.id === chatId);
          if (conversation) {
            setMessages(conversation.messages || []);
          }
        }
      } else {
        setMessages([]);
        setCurrentConversationId(null);
      }
    };
    loadMessages();
  }, [chatId, conversations]);

  const handleSendMessage = async (content, model = 'gemini-2.5-flash') => {
    let convId = currentConversationId;

    // Create new conversation if needed
    if (!convId) {
      try {
        const response = await api.post('/api/chat/conversations', { title: content.substring(0, 50) });
        convId = response.data.id;
        setCurrentConversationId(convId);
        navigate(`/dashboard/chat/${convId}`, { replace: true });
        if (refreshConversations) refreshConversations();
      } catch (error) {
        console.error('Failed to create conversation:', error);
        return;
      }
    }

    // Add user message to UI immediately
    const userMessage = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const response = await api.post(`/api/chat/conversations/${convId}/messages`, { content, model });

      const { assistant_message } = response.data;
      setMessages(prev => [...prev.slice(0, -1), response.data.user_message, assistant_message]);
      
      if (refreshConversations) refreshConversations();
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => [...prev, {
        id: `msg_${Date.now()}`,
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerate = useCallback(async (messageId) => {
    // Find the user message before this assistant message
    const msgIndex = messages.findIndex(m => m.id === messageId);
    if (msgIndex > 0) {
      const userMsg = messages[msgIndex - 1];
      if (userMsg.role === 'user') {
        // Remove the assistant message and regenerate
        setMessages(prev => prev.slice(0, msgIndex));
        await handleSendMessage(userMsg.content);
      }
    }
  }, [messages]);

  const handleDeleteMessage = useCallback((messageId) => {
    setMessages(prev => prev.filter(m => m.id !== messageId));
  }, []);

  return (
    <ChatInterface
      messages={messages}
      onSendMessage={handleSendMessage}
      onRegenerate={handleRegenerate}
      onDeleteMessage={handleDeleteMessage}
      isLoading={isLoading}
    />
  );
};

export default ChatPage;
